import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Plus, RefreshCw, Search, FileText, ChevronLeft, ChevronRight,
  CheckCircle2, Clock, AlertCircle, XCircle, BookCheck
} from 'lucide-react';

import { glJournalApi }    from '@/api/glJournal.api';
import { useContextScope, scopeFromSession } from '@/hooks/useContextScope';
import { ScopeChips } from '@/components/lists/ScopeChips';
import { usePermission }   from '@/hooks/usePermission';
import { Permissions }     from '@/constants/permissions';
import { AppRoutes }       from '@/constants/routes';
import { Spinner }         from '@/components/ui/Spinner';
import { cn }              from '@/utils/cn';
import type { RootState }  from '@/state/store';

import type { GlJournalListDto, GlJournalFilterDto, WorkflowStatus } from '@/types/glJournal.types';
import { WORKFLOW_STATUS_LABELS, WORKFLOW_STATUS_COLORS } from '@/types/glJournal.types';

// ── Status icon map ────────────────────────────────────────────────────────────

const StatusIcon = ({ status }: { status: WorkflowStatus }) => {
  const props = { size: 14, className: 'shrink-0' };
  switch (status) {
    case 'Draft':        return <FileText   {...props} className={`${props.className} text-gray-500 dark:text-gray-400`} />;
    case 'Submitted':    return <Clock      {...props} className={`${props.className} text-blue-500`} />;
    case 'Approved':     return <CheckCircle2 {...props} className={`${props.className} text-green-500`} />;
    case 'Posted':       return <BookCheck  {...props} className={`${props.className} text-emerald-600 dark:text-emerald-400`} />;
    case 'Rejected':     return <XCircle    {...props} className={`${props.className} text-red-500`} />;
    default:             return <AlertCircle {...props} className={`${props.className} text-amber-500`} />;
  }
};

// ── GL Journal List Page ───────────────────────────────────────────────────────

export const GlJournalListPage = () => {
  const navigate              = useNavigate();
  const { hasPermission }     = usePermission();
  const canCreate             = hasPermission(Permissions.GL_JOURNAL_CREATE);

  // Session context — used to default filters and show the context banner
  const session = useSelector((s: RootState) => s.session.session);

  const [items,    setItems]    = useState<GlJournalListDto[]>([]);
  const [loading,  setLoading]  = useState(false);
  const [total,    setTotal]    = useState(0);
  const [page,     setPage]     = useState(1);
  const pageSize = 20;

  // Status summary counters (derived from current page + carried totals)
  const statusCounts = {
    Draft:     items.filter(j => j.workflowStatus === 'Draft').length,
    Submitted: items.filter(j => j.workflowStatus === 'Submitted' || j.workflowStatus === 'PendingApproval').length,
    Approved:  items.filter(j => j.workflowStatus === 'Approved').length,
    Posted:    items.filter(j => j.workflowStatus === 'Posted').length,
    Rejected:  items.filter(j => j.workflowStatus === 'Rejected').length,
  };

  // Initialise filter — location and cost center are pre-scoped from session.
  // Financial year is NOT pre-filtered here: the backend enforces FY scope for
  // restricted users via the controller. Admins see all FYs by default.
  const [filter, setFilter] = useState<GlJournalFilterDto>(() => ({
    pageNumber:       1,
    pageSize,
    sortBy:           'documentDate',
    sortDescending:   true,
    ...scopeFromSession(session),
  }));

  // Re-sync location/CC filter defaults when session context changes mid-session.
  // FY is intentionally excluded — backend enforces FY scope for restricted users.
  const scope = useContextScope(s => {
    setFilter(f => ({ ...f, ...s, pageNumber: 1 }));
    setPage(1);
  });

  const [search, setSearch] = useState('');

  const load = useCallback(async (f: GlJournalFilterDto) => {
    setLoading(true);
    try {
      const res = await glJournalApi.getList(f);
      const data = res.data?.data;
      setItems(data?.items ?? []);
      setTotal(data?.totalCount ?? 0);
    } catch (err) {
      console.error('Failed to load GL journals', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(filter);
  }, [filter, load]);

  const handleSearch = () => {
    const newFilter = { ...filter, documentNumber: search || undefined, pageNumber: 1 };
    setFilter(newFilter);
    setPage(1);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    setFilter(f => ({ ...f, pageNumber: newPage }));
  };

  const handleStatusFilter = (status: string) => {
    setFilter(f => ({
      ...f,
      workflowStatus: f.workflowStatus === status ? undefined : status,
      pageNumber: 1,
    }));
    setPage(1);
  };

  const totalPages = Math.ceil(total / pageSize);

  // ── Context scope chips — shows what the list is currently filtered to ──────

  return (
    <div className="flex flex-col h-full bg-slate-50 dark:bg-slate-900">
      {/* ── Page Header ──────────────────────────────────────────────────────── */}
      <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold text-slate-900 dark:text-white">
              GL Journal Vouchers
            </h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
              {total.toLocaleString()} total entries
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => load(filter)}
              className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-500 dark:text-gray-400 transition-colors"
              title="Refresh"
            >
              <RefreshCw size={16} />
            </button>
            {canCreate && (
              <button
                onClick={() => navigate(AppRoutes.GL_JOURNAL_NEW)}
                className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
              >
                <Plus size={15} />
                New Journal
              </button>
            )}
          </div>
        </div>

        {/* ── Active context banner ─────────────────────────────────────────── */}
        <ScopeChips chips={scope.chips} canReset={scope.canReset} onReset={scope.reset} />

        {/* ── Status Summary Bar — each card is clickable to apply a status filter ── */}
        <div className="grid grid-cols-5 gap-2 mt-4">
          {([
            { label: 'Draft',    status: 'Draft',     count: statusCounts.Draft,     icon: <FileText    size={14} />, color: 'text-slate-500 dark:text-gray-400',   bg: 'bg-slate-50 dark:bg-slate-700/40',    ring: 'ring-slate-200'   },
            { label: 'Pending',  status: 'Submitted', count: statusCounts.Submitted, icon: <Clock       size={14} />, color: 'text-blue-600 dark:text-blue-400',    bg: 'bg-blue-50 dark:bg-blue-900/20',      ring: 'ring-blue-200'    },
            { label: 'Approved', status: 'Approved',  count: statusCounts.Approved,  icon: <CheckCircle2 size={14} />, color: 'text-green-600 dark:text-green-400', bg: 'bg-green-50 dark:bg-green-900/20',    ring: 'ring-green-200'   },
            { label: 'Posted',   status: 'Posted',    count: statusCounts.Posted,    icon: <BookCheck   size={14} />, color: 'text-emerald-600 dark:text-emerald-400', bg: 'bg-emerald-50 dark:bg-emerald-900/20', ring: 'ring-emerald-200' },
            { label: 'Rejected', status: 'Rejected',  count: statusCounts.Rejected,  icon: <XCircle     size={14} />, color: 'text-red-500',     bg: 'bg-red-50 dark:bg-red-900/20',        ring: 'ring-red-200'     },
          ] as const).map(stat => {
            const isActive = filter.workflowStatus === stat.status;
            return (
              <button
                key={stat.label}
                onClick={() => handleStatusFilter(stat.status)}
                title={isActive ? `Clear "${stat.label}" filter` : `Show only ${stat.label} journals`}
                className={cn(
                  'flex items-center gap-2 px-3 py-2 rounded-lg ring-1 transition-all text-left',
                  'hover:ring-2 hover:shadow-sm active:scale-95',
                  stat.bg, stat.ring,
                  isActive && 'ring-2 shadow-md scale-[0.98]',
                )}
              >
                <span className={stat.color}>{stat.icon}</span>
                <div>
                  <p className="text-[10px] text-slate-500 dark:text-slate-400 leading-none">{stat.label}</p>
                  <p className={cn('text-sm font-bold leading-tight', stat.color)}>{stat.count}</p>
                </div>
              </button>
            );
          })}
        </div>

        {/* ── Filters ──────────────────────────────────────────────────────────── */}
        <div className="flex items-center gap-3 mt-4">
          <div className="flex-1 max-w-xs relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSearch()}
              placeholder="Search by document #…"
              className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg
                         bg-white dark:bg-slate-700 text-slate-900 dark:text-white
                         focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
            />
          </div>

          <div className="flex items-center gap-1.5">
            {(['Draft', 'Submitted', 'Approved', 'Posted', 'Rejected'] as WorkflowStatus[]).map(s => (
              <button
                key={s}
                onClick={() => handleStatusFilter(s)}
                className={cn(
                  'px-3 py-1.5 text-xs font-medium rounded-full transition-all',
                  filter.workflowStatus === s
                    ? WORKFLOW_STATUS_COLORS[s] + ' ring-2 ring-offset-1 ring-current'
                    : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200'
                )}
              >
                {WORKFLOW_STATUS_LABELS[s]}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── Table ────────────────────────────────────────────────────────────── */}
      <div className="flex-1 overflow-auto">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Spinner size="lg" />
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-slate-400">
            <FileText size={40} className="mb-3 opacity-40" />
            <p className="text-sm font-medium">No journal entries found</p>
            {canCreate && (
              <button
                onClick={() => navigate(AppRoutes.GL_JOURNAL_NEW)}
                className="mt-3 text-blue-600 dark:text-blue-400 text-sm hover:underline"
              >
                Create your first journal
              </button>
            )}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-slate-50 dark:bg-slate-800/80 backdrop-blur border-b border-slate-200 dark:border-slate-700">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Document #</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Date</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Type</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Narration</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Debit</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Credit</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Currency</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Created By</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {items.map(j => (
                <tr
                  key={j.id}
                  onClick={() => navigate(`/gl/journals/${j.id}`)}
                  className="hover:bg-blue-50 dark:hover:bg-slate-700/50 cursor-pointer transition-colors group"
                >
                  <td className="px-4 py-3 font-mono font-semibold text-blue-700 dark:text-blue-400 group-hover:text-blue-800">
                    {j.documentNumber}
                  </td>
                  <td className="px-4 py-3 text-slate-700 dark:text-slate-300">
                    {new Date(j.documentDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                  </td>
                  <td className="px-4 py-3">
                    <span className="px-2 py-0.5 bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 rounded text-xs font-mono">
                      {j.transactionTypeCode}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-600 dark:text-slate-400 max-w-xs truncate">
                    {j.narration ?? j.reference ?? '—'}
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-slate-900 dark:text-white">
                    {j.totalDebit.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-slate-900 dark:text-white">
                    {j.totalCredit.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                  </td>
                  <td className="px-4 py-3 text-slate-500 dark:text-slate-400 font-mono text-xs">
                    {j.currencyCode}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-center gap-1.5">
                      <StatusIcon status={j.workflowStatus} />
                      <span className={cn(
                        'px-2 py-0.5 rounded-full text-xs font-medium',
                        WORKFLOW_STATUS_COLORS[j.workflowStatus]
                      )}>
                        {WORKFLOW_STATUS_LABELS[j.workflowStatus]}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-slate-500 dark:text-slate-400 text-xs">
                    {j.createdByName ?? '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Pagination ───────────────────────────────────────────────────────── */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-6 py-3">
          <p className="text-sm text-slate-500 dark:text-gray-400">
            Page {page} of {totalPages} · {total.toLocaleString()} records
          </p>
          <div className="flex items-center gap-1">
            <button
              onClick={() => handlePageChange(page - 1)}
              disabled={page <= 1}
              className="p-1.5 rounded hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft size={16} />
            </button>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const n = Math.max(1, Math.min(page - 2, totalPages - 4)) + i;
              return (
                <button
                  key={n}
                  onClick={() => handlePageChange(n)}
                  className={cn(
                    'w-8 h-8 text-sm rounded transition-colors',
                    n === page
                      ? 'bg-blue-600 text-white font-semibold'
                      : 'hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300'
                  )}
                >
                  {n}
                </button>
              );
            })}
            <button
              onClick={() => handlePageChange(page + 1)}
              disabled={page >= totalPages}
              className="p-1.5 rounded hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
